// eslint-disable import/no-cycle
import { Action, action, Thunk, thunk } from 'easy-peasy';
import { AxiosResponse } from 'axios';
import { HttpStoreModel } from '../../../http/models/httpModel';
import { AccountController } from '../../../controllers/accountController';
import HttpClientWithRetry from '../../../http/client/HttpClientWithRetry';
import { FavouriteSeeker } from '../../../firebase/firestore/documents/user';
import { BusinessManagementController } from '../../../controllers/businessManagementController';
import { BusinessListQueryParams } from '../../administration/positionManagement/BusinessType';
import { CandidateListType, SearchAndSortTypes } from '../sidebar/filters/seekersFiltersContextValueType';
import {
  CreateTemplateSettingsRequest,
  TemplateSettingResponse,
} from '../../administration/hireForm/templateSettings/templateSettings.interface';
import { OfferConditionsType } from '../../../firebase/firestore/documents/candidateAudit';
import {
  QuestionnaireTemplateResponse,
  QuestionTemplatePagination,
  QuestionTemplateListQuery,
  QuestionnaireTemplateListResponse,
} from '../../administration/questionnaireTemplate/questionnaireTemplate.types';

export type AccountStoreModel = {
  account: AccountModel;
};
export interface AccountModel {
  currentAccountInvite?: { inviteId: string; accountId: string };
  setCurrentAccountInvite: Action<AccountModel, { inviteId: string; accountId: string }>;
  getFavoriteSeekersList: Thunk<
    AccountModel,
    { accountId: string; userId: string },
    void,
    HttpStoreModel,
    Promise<FavouriteSeeker[]>
  >;
  saveNote: Thunk<AccountModel, { accountId: string; positionId: string; seekerId: string; note: string }, void, HttpStoreModel>;
  updateNote: Thunk<
    AccountModel,
    { accountId: string; positionId: string; seekerId: string; noteIndex: number; note?: string },
    void,
    HttpStoreModel
  >;
  deleteNote: Thunk<
    AccountModel,
    { accountId: string; positionId: string; seekerId: string; noteIndex: number },
    void,
    HttpStoreModel
  >;
  saveOfferMadeNote: Thunk<
    AccountModel,
    {
      positionId: string;
      seekerId: string;
      offerMade: string;
      salary: string | null;
      salaryUnit: string | null;
      customSalaryUnit?: string | null;
      sendMessageToCandidates?: boolean;
      requisitionId?: string;
      updatedPositionId?: string;
      updatedBusinessId?: string;
      autoDismiss?: boolean;
      offerConditions?: OfferConditionsType;
      employmentStartDate?: Date;
    },
    void,
    HttpStoreModel
  >;
  saveOfferRejectedNote: Thunk<
    AccountModel,
    { positionId: string; seekerId: string; offerRejectedDetails: string | null },
    void,
    HttpStoreModel
  >;
  addSeekerToFavorite: Thunk<AccountModel, { accountId: string; userId: string; seekerId: string }, void, HttpStoreModel>;
  removeSeekerFromFavorite: Thunk<AccountModel, { accountId: string; userId: string; seekerId: string }, void, HttpStoreModel>;
  updateHireWebHookUrlInAccount: Thunk<AccountModel, { accountId: string; link: string }, void, HttpStoreModel>;
  getAllFollowedPositionOfAccount: Thunk<AccountModel, { accountId: string }, void, HttpStoreModel>;
  getAllBusinessWithOpenPositions: Thunk<
    AccountModel,
    { params: BusinessListQueryParams & { listType: CandidateListType; advancedFilters: SearchAndSortTypes } },
    void,
    HttpStoreModel
  >;
  getOrganizationHireFormSettings: Thunk<
    AccountModel,
    { accountId: string },
    void,
    HttpStoreModel,
    Promise<AxiosResponse<TemplateSettingResponse>>
  >;
  postOrganizationHireFormSettings: Thunk<
    AccountModel,
    {
      accountId: string;
      hireFormSettings: CreateTemplateSettingsRequest;
    },
    void,
    HttpStoreModel,
    Promise<AxiosResponse>
  >;
  switchAccount: Thunk<AccountModel, { accountInviteId: string; userId: string }, void, HttpStoreModel>;
  checkUserExists: Thunk<AccountModel, { userId: string }, void, HttpStoreModel, Promise<AxiosResponse>>;
  questionTemplateList: Thunk<
    AccountModel,
    QuestionTemplateListQuery,
    void,
    HttpStoreModel,
    Promise<AxiosResponse<QuestionTemplatePagination>>
  >;
  questionTemplates: QuestionnaireTemplateListResponse[];
  setQuestionTemplates: Action<AccountModel, QuestionnaireTemplateListResponse[]>;
  questionTemplate?: QuestionnaireTemplateResponse;
  setQuestionTemplate: Action<AccountModel, QuestionnaireTemplateResponse | undefined>;
  selectedQuestionTemplateId?: string;
  setSelectedQuestionTemplateId: Action<AccountModel, string | undefined>;
  getQuestionTemplateById: Thunk<
    AccountModel,
    { accountId: string; id: string },
    void,
    HttpStoreModel,
    Promise<AxiosResponse<QuestionnaireTemplateResponse>>
  >;
}

export const accountStoreModel: AccountModel = {
  questionTemplates: [],
  setQuestionTemplates: action((state, payload) => {
    state.questionTemplates = payload;
  }),
  currentAccountInvite: undefined,
  setCurrentAccountInvite: action((state, payload) => {
    state.currentAccountInvite = payload;
  }),
  questionTemplate: undefined,
  setQuestionTemplate: action((state, payload) => {
    state.questionTemplate = payload;
  }),
  selectedQuestionTemplateId: undefined,
  setSelectedQuestionTemplateId: action((state, payload) => {
    state.selectedQuestionTemplateId = payload;
  }),
  getFavoriteSeekersList: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.getFavoriteSeekersList(payload.accountId, payload.userId);
    return response?.data as FavouriteSeeker[];
  }),

  saveNote: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    await controller.saveNote(payload.accountId, payload.positionId, payload.seekerId, payload.note);
  }),

  updateNote: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    await controller.updateNote(payload.accountId, payload.positionId, payload.seekerId, payload.noteIndex, payload.note);
  }),

  deleteNote: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    await controller.deleteNote(payload.accountId, payload.positionId, payload.seekerId, payload.noteIndex);
  }),

  addSeekerToFavorite: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    await controller.addSeekerToFavorite(payload.accountId, payload.userId, payload.seekerId);
  }),

  removeSeekerFromFavorite: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    await controller.removeSeekerFromFavorite(payload.accountId, payload.userId, payload.seekerId);
  }),

  saveOfferMadeNote: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    await controller.saveOfferMadeNote(
      payload.positionId,
      payload.seekerId,
      payload.offerMade,
      payload?.salary,
      payload.salaryUnit,
      payload.customSalaryUnit,
      payload.sendMessageToCandidates,
      payload.requisitionId,
      payload.updatedPositionId,
      payload.updatedBusinessId,
      payload.autoDismiss,
      payload.offerConditions,
      payload.employmentStartDate,
    );
  }),

  saveOfferRejectedNote: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    await controller.saveOfferRejectedNote(payload.positionId, payload.seekerId, payload.offerRejectedDetails);
  }),
  updateHireWebHookUrlInAccount: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    await controller.updateHireWebHookUrlInAccount(payload.accountId, payload.link);
  }),

  getAllFollowedPositionOfAccount: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.getAllFollowedPositionOfAccount(payload.accountId);
    return response;
  }),

  getAllBusinessWithOpenPositions: /* istanbul ignore next */ thunk(
    /* istanbul ignore next */
    async (_actions, payload, { getStoreState }) => {
      const controller = new BusinessManagementController(getStoreState().http.client as HttpClientWithRetry);
      const response = await controller.getBusinessWithOpenPositions(payload.params);
      return response;
    },
  ),

  getOrganizationHireFormSettings: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.getOrganizationHireFormSettings(payload.accountId);
    return response;
  }),
  postOrganizationHireFormSettings: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.postOrganizationHireFormSettings(payload.accountId, payload.hireFormSettings);
    return response;
  }),

  switchAccount: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.switchAccount(payload.accountInviteId, payload.userId);
    return response;
  }),

  checkUserExists: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.checkUserExists(payload.userId);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return
    return response?.data?.exists;
  }),
  questionTemplateList: thunk((_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);

    return controller.getQuestionTemplateList(payload.accountId, {
      limit: payload.limit ?? undefined,
      page: payload.page,
      before: payload.before ?? undefined,
      after: payload.after ?? undefined,
      sortField: payload.sortField,
      sortDirection: payload.sortDirection,
      type: payload.type ?? undefined,
      searchQuery: payload.searchQuery ?? undefined,
    });
  }),
  getQuestionTemplateById: thunk(async (actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.getQuestionTemplateById(payload.accountId, payload.id);
    actions.setQuestionTemplate(response.data);
    return response;
  }),
};
